import { Command, CommandEmpty, CommandInput, CommandList } from "./command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu";
import { SidebarMenuButton } from "./sidebar";
import { ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { Shop, ShopLogo } from "./shop-logo";
import { PersonalAccount, PersonalAccountProps } from "./personal-account";
import ShopsList, { ShopsListProps } from "./shops-list";



export interface User {
  emails?: Array<{ address: string }>;
}



export interface AccountSwitcherProps {
  currentShop: Shop;
  user: User | null;
  shops: Shop[];
  onShopSelect: (shop: Shop) => void;
  onSettingsClick: () => void;
  onLogout: () => void;
  noResultLabel?: string;
  settingsLabel?: string;
  logoutLabel?: string;
  shopsHeading?: string;
  settingsHeading?: string;
}



export default function AccountSwitcher({
                                          currentShop,
                                          user,
                                          shops,
                                          onShopSelect,
                                          onSettingsClick,
                                          onLogout,
                                          noResultLabel = "No results found",
                                          settingsLabel = "Account Preferences",
                                          logoutLabel = "Disconnect",
                                          shopsHeading = "Dashboard",
                                          settingsHeading = "Settings",
                                        }: AccountSwitcherProps) {
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const shopsListProps: ShopsListProps = {
    shops,
    currentShop,
    onShopSelect: (shop: Shop) => {
      onShopSelect(shop);
      setOpen(false);
    },
    heading: shopsHeading,
  };

  const personalAccountProps: PersonalAccountProps = {
    onSettingsClick: () => {
      onSettingsClick();
      setOpen(false);
    },
    onLogout: () => {
      onLogout();
      setOpen(false);
    },
    settingsLabel,
    logoutLabel,
    heading: settingsHeading,
  };

  const name = user?.emails?.[0]?.address ?? "";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <ShopLogo shop={currentShop} />
          <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentShop.name}
                </span>
            <span className="truncate text-xs">{name}</span>
          </div>
          <ChevronsUpDown className="ml-auto" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-72 rounded-lg p-0"
        side="bottom"
        align="start"
        sideOffset={4}
      >
        <Command
          filter={(value, search) => {
            const normalizeText = (text: string) =>
              text
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");

            const normalizedValue = normalizeText(value);
            const normalizedSearch = normalizeText(search);
            const match = normalizedValue.includes(normalizedSearch);
            return match ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder={currentShop.name}
            autoFocus
            className="border-0 focus:ring-0"
          />
          <div className="flex flex-col max-h-[400px]">
            <CommandList className="max-h-[300px] overflow-y-auto">
              <CommandEmpty>{noResultLabel}</CommandEmpty>
              <ShopsList {...shopsListProps} />
            </CommandList>
            <div className="border-t">
              <PersonalAccount {...personalAccountProps} />
            </div>
          </div>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
